import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Check, Sparkles, Search, Grid3X3, LayoutGrid,
  Monitor, Smartphone, Tablet, Eye, X
} from 'lucide-react';
import { TEMPLATES, TEMPLATE_CATEGORIES, type Template } from '@/lib/templates';
import { renderTemplate } from '@shared/siteRenderer';
import { Button } from '@/components/ui/Button';
import { DashboardBackground } from '@/components/three';

// Category descriptions for better UX
const CATEGORY_DESCRIPTIONS: Record<string, { description: string; icon: string }> = {
  Modern: { description: 'Clean gradients, glass effects, and contemporary aesthetics', icon: '✨' },
  Classic: { description: 'Timeless elegance with refined typography', icon: '🏛️' },
  Bold: { description: 'High contrast, striking colors, and impactful designs', icon: '⚡' },
  Minimal: { description: 'Simple, clean, and focused on content', icon: '◻️' },
  Creative: { description: 'Playful colors, unique layouts, and artistic flair', icon: '🎨' },
  Corporate: { description: 'Professional and trustworthy business presence', icon: '💼' },
  Luxury: { description: 'Premium aesthetics with gold accents and dark themes', icon: '👑' },
  Tech: { description: 'Futuristic, digital, and cutting-edge', icon: '🚀' },
  Health: { description: 'Calming, natural, and wellness-focused', icon: '🌿' },
  Food: { description: 'Warm, inviting, and appetite-inspiring', icon: '🍽️' },
};

// Template Preview Card
function TemplatePreviewCard({
  template,
  isSelected,
  onSelect,
  onPreview,
}: {
  template: Template;
  isSelected: boolean;
  onSelect: () => void;
  onPreview: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className={`
        relative rounded-2xl overflow-hidden cursor-pointer group
        transition-all duration-300
        ${isSelected
          ? 'ring-4 ring-primary-500 ring-offset-2 ring-offset-[#1a1a2e] shadow-xl shadow-primary-500/20'
          : 'ring-1 ring-white/10 hover:ring-white/30'
        }
      `}
      onClick={onSelect}
    >
      {/* Preview Image */}
      <div className="aspect-[4/3] relative overflow-hidden">
        <div
          className="absolute inset-0 transition-transform duration-500 group-hover:scale-105"
          style={{ background: template.preview }}
        />

        {/* Hover overlay with preview button */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
            className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg text-white text-sm font-medium hover:bg-white/20 transition-colors flex items-center gap-2"
          >
            <Eye size={16} />
            Preview
          </button>
        </div>

        {/* Selected checkmark */}
        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute top-3 right-3 w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center shadow-lg"
            >
              <Check size={18} className="text-white" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Layout badge */}
        <div className="absolute bottom-3 left-3 px-2 py-1 bg-black/50 backdrop-blur-sm rounded text-white/80 text-[10px] font-medium uppercase tracking-wider">
          {template.layout.replace('hero-', '')}
        </div>
      </div>

      {/* Info section */}
      <div className="p-4 bg-[#16162a]/80 backdrop-blur-sm">
        <h3 className="font-semibold text-white text-sm">{template.name}</h3>
        <div className="flex items-center justify-between mt-1">
          <p className="text-white/50 text-xs">{template.category}</p>
          <div className="flex items-center gap-1">
            <span
              className="w-3 h-3 rounded-full"
              style={{ background: `hsl(${template.theme.primary})` }}
            />
            <span
              className="w-3 h-3 rounded-full"
              style={{ background: `hsl(${template.theme.accent})` }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Full Preview Modal
function PreviewModal({
  template,
  onClose,
  onSelect,
}: {
  template: Template;
  onClose: () => void;
  onSelect: () => void;
}) {
  const [viewMode, setViewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const previewHtml = useMemo(() => renderTemplate(template.id, {
    businessName: 'Your Brand',
    headline: 'Transform Your Business',
    aboutText: 'We provide professional services with a focus on quality, reliability, and measurable results for every client.',
    services: [
      { name: 'Strategy', description: 'Clear planning tailored to your goals.' },
      { name: 'Execution', description: 'Expert implementation from start to finish.' },
      { name: 'Support', description: 'Reliable guidance and optimization over time.' },
    ],
    testimonials: [
      { name: 'Alex Parker', role: 'Client', content: 'Excellent communication and outstanding outcomes.' },
      { name: 'Jordan Lee', role: 'Founder', content: 'Fast, professional, and easy to work with.' },
    ],
    seo: {
      title: `${template.name} Template Preview`,
      description: `Live preview for the ${template.name} template.`,
    },
    businessData: {
      rating: 4.9,
      reviewCount: 127,
      address: '123 Main Street, Your City',
      phone: '(555) 123-4567',
      photos: [
        'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80',
        'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1200&q=80',
      ],
    },
  }), [template]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-6xl bg-[#16162a] rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="h-16 border-b border-white/10 flex items-center justify-between px-6">
          <div>
            <h2 className="text-white font-semibold">{template.name}</h2>
            <p className="text-white/50 text-sm">{template.category} • {template.layout}</p>
          </div>
          <div className="flex items-center gap-4">
            {/* View mode toggle */}
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
              {[
                { mode: 'desktop' as const, icon: Monitor },
                { mode: 'tablet' as const, icon: Tablet },
                { mode: 'mobile' as const, icon: Smartphone },
              ].map(({ mode, icon: Icon }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === mode
                      ? 'bg-primary-500/30 text-primary-400'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <Icon size={18} />
                </button>
              ))}
            </div>
            <Button onClick={onSelect} className="bg-primary-500 hover:bg-primary-600">
              Use This Theme
            </Button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Preview Area */}
        <div className="h-[70vh] bg-[#0f0f1a] overflow-auto flex items-start justify-center p-8">
          <div
            className="bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300"
            style={{
              width: viewMode === 'desktop' ? '100%' : viewMode === 'tablet' ? '768px' : '375px',
              maxWidth: '100%',
            }}
          >
            <iframe
              srcDoc={previewHtml}
              title={`${template.name} preview`}
              className="w-full border-0"
              style={{ height: '70vh', background: '#000' }}
              sandbox="allow-scripts"
            />
          </div>
        </div>

        {/* Theme Details */}
        <div className="h-20 border-t border-white/10 flex items-center justify-between px-6 bg-[#12121f]">
          <div className="flex items-center gap-6">
            <div>
              <span className="text-white/50 text-xs block mb-1">Typography</span>
              <span className="text-white text-sm">{template.typography.fontHeading} / {template.typography.fontBody}</span>
            </div>
            <div className="h-8 w-px bg-white/10" />
            <div>
              <span className="text-white/50 text-xs block mb-1">Style</span>
              <span className="text-white text-sm capitalize">{template.style.cardStyle} cards, {template.style.headerStyle} header</span>
            </div>
            <div className="h-8 w-px bg-white/10" />
            <div>
              <span className="text-white/50 text-xs block mb-1">Colors</span>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded" style={{ background: `hsl(${template.theme.primary})` }} />
                <span className="w-4 h-4 rounded" style={{ background: `hsl(${template.theme.secondary})` }} />
                <span className="w-4 h-4 rounded" style={{ background: `hsl(${template.theme.accent})` }} />
                <span className="w-4 h-4 rounded" style={{ background: `hsl(${template.theme.background})` }} />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function ThemeSelectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('leadId');

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Filter templates
  const filteredTemplates = useMemo(() => {
    let templates = TEMPLATES;

    if (selectedCategory) {
      templates = templates.filter(t => t.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      templates = templates.filter(t =>
        t.name.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query) ||
        t.layout.toLowerCase().includes(query)
      );
    }

    return templates;
  }, [selectedCategory, searchQuery]);

  // Handle template selection and continue
  const handleContinue = () => {
    if (!selectedTemplate) return;
    // Navigate to site creation with selected template
    const params = new URLSearchParams();
    params.set('template', selectedTemplate);
    if (leadId) params.set('leadId', leadId);
    navigate(`/dashboard/sites/create?${params.toString()}`);
  };

  return (
    <DashboardBackground>
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className="h-16 border-b border-white/10 flex items-center justify-between px-6 bg-black/30 backdrop-blur-md flex-shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-white font-semibold">Choose a Theme</h1>
              <p className="text-white/50 text-sm">Select from 50 professionally designed templates</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search themes..."
                className="w-64 pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-primary-500/50"
              />
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white'}`}
              >
                <Grid3X3 size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white'}`}
              >
                <LayoutGrid size={18} />
              </button>
            </div>

            {/* Continue button */}
            <Button
              onClick={handleContinue}
              disabled={!selectedTemplate}
              className="bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
            >
              Continue
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">
          {/* Category Sidebar */}
          <aside className="w-64 border-r border-white/10 bg-black/20 flex-shrink-0 overflow-y-auto">
            <div className="p-4">
              <h2 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-4">Categories</h2>

              {/* All Templates */}
              <button
                onClick={() => setSelectedCategory(null)}
                className={`w-full flex items-center justify-between p-3 rounded-xl mb-2 transition-colors ${
                  selectedCategory === null
                    ? 'bg-primary-500/20 border border-primary-500/40 text-white'
                    : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Sparkles size={18} />
                  <span className="font-medium">All Themes</span>
                </div>
                <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{TEMPLATES.length}</span>
              </button>

              {/* Category List */}
              <div className="space-y-1">
                {TEMPLATE_CATEGORIES.map(category => {
                  const count = TEMPLATES.filter(t => t.category === category).length;
                  const info = CATEGORY_DESCRIPTIONS[category];
                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`w-full text-left p-3 rounded-xl transition-colors ${
                        selectedCategory === category
                          ? 'bg-primary-500/20 border border-primary-500/40'
                          : 'bg-white/5 border border-transparent hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span>{info?.icon}</span>
                          <span className={`font-medium ${selectedCategory === category ? 'text-white' : 'text-white/80'}`}>
                            {category}
                          </span>
                        </div>
                        <span className="text-xs text-white/40">{count}</span>
                      </div>
                      <p className="text-xs text-white/40 line-clamp-1">{info?.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Template Grid */}
          <main className="flex-1 overflow-y-auto p-6">
            {/* Category Header (when category selected) */}
            {selectedCategory && (
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{CATEGORY_DESCRIPTIONS[selectedCategory]?.icon}</span>
                  <h2 className="text-2xl font-bold text-white">{selectedCategory}</h2>
                </div>
                <p className="text-white/60">{CATEGORY_DESCRIPTIONS[selectedCategory]?.description}</p>
              </div>
            )}

            {/* Results count */}
            <p className="text-white/50 text-sm mb-4">
              {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} found
            </p>

            {/* Template Grid */}
            <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
              {filteredTemplates.map((template) => (
                <TemplatePreviewCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplate === template.id}
                  onSelect={() => setSelectedTemplate(template.id)}
                  onPreview={() => setPreviewTemplate(template)}
                />
              ))}
            </div>

            {/* Empty state */}
            {filteredTemplates.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <Search size={24} className="text-white/40" />
                </div>
                <h3 className="text-white font-semibold mb-2">No templates found</h3>
                <p className="text-white/50 text-sm">Try adjusting your search or category filter</p>
              </div>
            )}
          </main>
        </div>

        {/* Preview Modal */}
        <AnimatePresence>
          {previewTemplate && (
            <PreviewModal
              template={previewTemplate}
              onClose={() => setPreviewTemplate(null)}
              onSelect={() => {
                setSelectedTemplate(previewTemplate.id);
                setPreviewTemplate(null);
              }}
            />
          )}
        </AnimatePresence>

        {/* Bottom bar - selected template info */}
        <AnimatePresence>
          {selectedTemplate && (
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="h-20 border-t border-white/10 bg-[#16162a]/95 backdrop-blur-md flex items-center justify-between px-6"
            >
              <div className="flex items-center gap-4">
                {(() => {
                  const template = TEMPLATES.find(t => t.id === selectedTemplate);
                  if (!template) return null;
                  return (
                    <>
                      <div
                        className="w-16 h-12 rounded-lg"
                        style={{ background: template.preview }}
                      />
                      <div>
                        <h3 className="text-white font-semibold">{template.name}</h3>
                        <p className="text-white/50 text-sm">{template.category} • {template.layout}</p>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedTemplate(null)}
                  className="text-white/60 hover:text-white"
                >
                  Clear Selection
                </Button>
                <Button
                  onClick={handleContinue}
                  className="bg-primary-500 hover:bg-primary-600 px-8"
                >
                  Continue with this theme
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardBackground>
  );
}
